import { supabase } from '../lib/supabase';

export interface MatchUpdateData {
  start_date?: string;
  tournament_id?: string;
  name?: string;
  venue?: string;
  status?: string;
}

export interface OfficialUpdateData {
  match_id: string;
  officials: {
    referee?: string;
    assistant1?: string;
    assistant2?: string;
    var?: string;
    var_assistant?: string;
    fourth?: string;
  };
}

export const matchUpdateService = {
  // Update match basic information
  async updateMatchDetails(matchId: string, data: MatchUpdateData) {
    try {
      const { data: result, error } = await supabase
        .from('matches')
        .update(data)
        .eq('id', matchId)
        .select();

      if (error) {
        console.error('Error updating match details:', error);
        throw error;
      }

      return result;
    } catch (error) {
      console.error('Error updating match details:', error);
      throw error;
    }
  },

  // Update match officials - ensures 6 rows exist for each match (one per role)
  async updateMatchOfficials(matchId: string, officials: OfficialUpdateData['officials']) {
    try {
      console.log('🔍 Updating match officials for match:', matchId);
      console.log('📋 Officials data:', officials);

      // Check if match exists in matches table
      const { data: matchExists, error: matchCheckError } = await supabase
        .from('matches')
        .select('id')
        .eq('id', matchId)
        .single();

      if (matchCheckError) {
        console.error('❌ Error checking match existence:', matchCheckError);
        throw new Error(`Match check failed: ${matchCheckError.message}`);
      }

      if (!matchExists) {
        console.error('❌ Match not found:', matchId);
        throw new Error('Match not found');
      }

      console.log('✅ Match exists:', matchExists);

      // Get existing officials for this match
      const { data: existingOfficials, error: fetchError } = await supabase
        .from('match_officials')
        .select('*')
        .eq('match_id', matchId);

      if (fetchError) {
        console.error('❌ Error fetching existing officials:', fetchError);
        throw fetchError;
      }

      console.log('📊 Existing officials:', existingOfficials);

      // Only process roles that are actually being updated
      const rolesToUpdate = Object.keys(officials);
      console.log('🔄 Only updating these roles:', rolesToUpdate);

      // Process each role that needs updating
      const results = [];
      
      for (const role of rolesToUpdate) {
        const officialData = {
          match_id: matchId,
          official_id: officials[role as keyof typeof officials] || null,
          role: role,
          is_main_referee: role === 'referee'
        };
        const existingOfficial = existingOfficials?.find(
          existing => existing.role === officialData.role
        );

        if (existingOfficial) {
          // Update existing official
          console.log(`🔄 Updating existing ${officialData.role}:`, existingOfficial.id);
          
          const { data: updateResult, error: updateError } = await supabase
            .from('match_officials')
            .update({ 
              official_id: officialData.official_id,
              is_main_referee: officialData.is_main_referee
            })
            .eq('id', existingOfficial.id)
            .select();

          if (updateError) {
            console.error(`❌ Error updating ${officialData.role}:`, updateError);
            throw updateError;
          }

          console.log(`✅ Updated ${officialData.role}:`, updateResult[0]);
          results.push(updateResult[0]);
        } else {
          // Insert new official (even if official_id is null)
          console.log(`📝 Inserting new ${officialData.role}:`, officialData);
          
          const { data: insertResult, error: insertError } = await supabase
            .from('match_officials')
            .insert([officialData])
            .select();

          if (insertError) {
            console.error(`❌ Error inserting ${officialData.role}:`, insertError);
            throw insertError;
          }

          console.log(`✅ Inserted ${officialData.role}:`, insertResult[0]);
          results.push(insertResult[0]);
        }
      }

      console.log('🎉 Successfully processed all 6 match officials:', results);
      return results;
    } catch (error) {
      console.error('❌ Error updating match officials:', error);
      throw error;
    }
  },

  // Update both match details and officials in one transaction
  async updateMatchAndOfficials(matchId: string, matchData: MatchUpdateData, officials: OfficialUpdateData['officials']) {
    try {
      // Update match details
      const matchResult = await this.updateMatchDetails(matchId, matchData);
      
      // Update officials
      const officialsResult = await this.updateMatchOfficials(matchId, officials);

      return {
        match: matchResult,
        officials: officialsResult
      };
    } catch (error) {
      console.error('Error updating match and officials:', error);
      throw error;
    }
  }
};
